import React from 'react'
import {
    Box,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import DeleteIcon from '@material-ui/icons/Delete'
import VisibilityIcon from '@material-ui/icons/Visibility'
import ShareIcon from '@material-ui/icons/Share'
import RefreshIcon from '@material-ui/icons/Refresh'
import EditIcon from '@material-ui/icons/Edit'
import {
    ApartmentContract,
    ApartmentContractType,
    ContractPaymentTerm,
} from '../../models/Company/types'
import {
    ContractStatusChip,
    lifecycleRowClassName,
    lifecycleRowShadeStyles,
} from './ContractStatusChip'

const MAX_PROPERTY_NAMES_VISIBLE = 2

function PropertyNamesCell({ names }: { names: string[] }) {
    if (!names.length) return <>—</>
    if (names.length <= MAX_PROPERTY_NAMES_VISIBLE) {
        return <>{names.join(', ')}</>
    }
    const visible = names.slice(0, MAX_PROPERTY_NAMES_VISIBLE).join(', ')
    const rest = names.length - MAX_PROPERTY_NAMES_VISIBLE
    return (
        <Tooltip title={names.join(', ')} placement="top" enterDelay={200}>
            <span style={{ cursor: 'default' }}>
                {visible}
                <Typography
                    component="span"
                    color="primary"
                    style={{
                        fontWeight: 600,
                        marginLeft: 4,
                        cursor: 'default',
                    }}
                >
                    +{rest} more
                </Typography>
            </span>
        </Tooltip>
    )
}

interface ContractsTableProps {
    contracts: ApartmentContract[]
    onDelete: (id: number) => void
    onView: (contract: ApartmentContract) => void
    onShare: (contract: ApartmentContract) => void
    onReanalyze: (id: number) => void
    onEdit: (contract: ApartmentContract) => void
    processingIds?: Set<number>
    // Pagination props
    totalCount: number
    page: number
    rowsPerPage: number
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => void
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
    emptyMessage?: string
}

const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    tableContainer: {
        flex: 1,
        maxHeight: 'calc(100vh - 550px)',
        overflow: 'auto',
    },
    table: {
        minWidth: 900,
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    ...lifecycleRowShadeStyles,
    iconCell: {
        width: 50,
    },
    contractIcon: {
        height: 32,
        width: 32,
        borderRadius: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.primary.light + '20',
        color: theme.palette.primary.main,
    },
    nameCell: {
        fontWeight: 500,
    },
    cellText: {
        fontSize: '14px',
    },
    summaryCell: {
        color: theme.palette.text.secondary,
        fontSize: '14px',
        maxWidth: 200,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontSize: '14px',
        fontStyle: 'italic',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    actionButton: {
        padding: theme.spacing(1),
    },
    shareButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    viewButton: {
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light + '20',
        },
    },
    reanalyzeButton: {
        color: theme.palette.secondary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.light + '20',
        },
    },
    deleteButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.error.main,
            backgroundColor: theme.palette.error.light + '20',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(6),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
    },
    emptyText: {
        color: theme.palette.text.secondary,
    },
    pagination: {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    statusActive: {
        backgroundColor: '#dcfce7',
        color: '#166534',
        fontWeight: 600,
        fontSize: '12px',
    },
    statusEndingSoon: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontWeight: 600,
        fontSize: '12px',
    },
    statusAutoRenew: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
        fontWeight: 600,
        fontSize: '12px',
    },
    statusExpired: {
        backgroundColor: '#fce4ec',
        color: '#b71c1c',
        fontWeight: 600,
        fontSize: '12px',
    },
    statusNoDates: {
        backgroundColor: '#f5f5f5',
        color: '#757575',
        fontWeight: 600,
        fontSize: '12px',
    },
}))

// Payment term display labels
const PAYMENT_TERM_LABELS: Record<ContractPaymentTerm, string> = {
    [ContractPaymentTerm.MONTHLY]: 'Monthly',
    [ContractPaymentTerm.QUARTERLY]: 'Quarterly',
    [ContractPaymentTerm.ANNUALLY]: 'Annually',
    [ContractPaymentTerm.ONE_TIME]: 'One-Time',
    [ContractPaymentTerm.PER_JOB]: 'Per Job',
}

const CONTRACT_TYPE_LABELS: Record<ApartmentContractType, string> = {
    [ApartmentContractType.CERTIFICATE]: 'Certificates',
    [ApartmentContractType.TURN]: 'Turn Vendors',
    [ApartmentContractType.GENERAL_VENDOR]: 'General Vendor',
}

export const ContractsTable: React.FC<ContractsTableProps> = ({
    contracts,
    onDelete,
    onView,
    onShare,
    onReanalyze,
    onEdit,
    processingIds = new Set(),
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    emptyMessage = 'No contracts found.',
}) => {
    const classes = useStyles()

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '—'
        // Parse date parts directly to avoid timezone conversion issues
        const [year, month, day] = dateString.split('T')[0].split('-')
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ]
        return `${months[parseInt(month, 10) - 1]} ${parseInt(
            day,
            10,
        )}, ${year}`
    }

    const formatCurrency = (value: number | null) => {
        if (value === null) return '—'
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value)
    }

    const formatPaymentTerm = (term: ContractPaymentTerm | null) => {
        if (!term) return '—'
        return PAYMENT_TERM_LABELS[term] || '—'
    }

    const formatContractType = (type?: ApartmentContractType) => {
        if (!type) {
            return CONTRACT_TYPE_LABELS[ApartmentContractType.GENERAL_VENDOR]
        }
        return CONTRACT_TYPE_LABELS[type] || type
    }

    if (totalCount === 0) {
        return (
            <Box className={classes.emptyState}>
                <Typography className={classes.emptyText}>
                    {emptyMessage}
                </Typography>
            </Box>
        )
    }

    return (
        <Paper className={classes.wrapper}>
            <TableContainer className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                    <TableHead>
                        <TableRow className={classes.headerRow}>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 50 }}
                            />
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 200 }}
                            >
                                Contract Name
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 130 }}
                            >
                                Type
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 150 }}
                            >
                                Vendor
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 140 }}
                            >
                                Properties
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 110 }}
                            >
                                Start Date
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 110 }}
                            >
                                End Date
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 110 }}
                            >
                                Status
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 100 }}
                            >
                                Payment
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 100 }}
                            >
                                Amount
                            </TableCell>
                            <TableCell className={classes.headerCell}>
                                Summary
                            </TableCell>
                            <TableCell
                                className={classes.headerCell}
                                style={{ width: 160 }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contracts.map((contract) => (
                            <TableRow
                                key={contract.id}
                                className={lifecycleRowClassName(
                                    classes,
                                    contract.lifecycle_status,
                                )}
                                onClick={() => onView(contract)}
                            >
                                <TableCell className={classes.iconCell}>
                                    <Box className={classes.contractIcon}>
                                        <DescriptionIcon
                                            style={{ fontSize: 18 }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell className={classes.nameCell}>
                                    {contract.name || '—'}
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    <Typography variant="body2">
                                        {(contract.contract_type ===
                                        ApartmentContractType.CERTIFICATE
                                            ? contract.organization_certificate_type_detail
                                            : contract.organization_contract_type_detail
                                        )?.name || 'Untyped'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {formatContractType(
                                            contract.contract_type,
                                        )}
                                    </Typography>
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    {contract.vendor?.name || '—'}
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    <PropertyNamesCell
                                        names={contract.apartment_names ?? []}
                                    />
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    {formatDate(contract.start_date)}
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    {formatDate(contract.end_date)}
                                </TableCell>
                                <TableCell>
                                    <ContractStatusChip
                                        status={contract.lifecycle_status}
                                        autoRenew={contract.auto_renew}
                                        endDate={contract.end_date}
                                    />
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    {formatPaymentTerm(contract.payment_term)}
                                </TableCell>
                                <TableCell className={classes.cellText}>
                                    {formatCurrency(contract.payment_amount)}
                                </TableCell>
                                <TableCell className={classes.summaryCell}>
                                    {processingIds.has(contract.id) ? (
                                        <Box
                                            className={
                                                classes.analyzingContainer
                                            }
                                        >
                                            <CircularProgress size={16} />
                                            <Typography
                                                className={
                                                    classes.analyzingText
                                                }
                                            >
                                                Analyzing...
                                            </Typography>
                                        </Box>
                                    ) : (
                                        contract.ai_summary || '—'
                                    )}
                                </TableCell>
                                <TableCell
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <Box className={classes.actions}>
                                        <Tooltip title="Share Link">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onShare(contract)
                                                }
                                                className={`${classes.actionButton} ${classes.shareButton}`}
                                            >
                                                <ShareIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => onView(contract)}
                                                className={`${classes.actionButton} ${classes.viewButton}`}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => onEdit(contract)}
                                                className={`${classes.actionButton} ${classes.viewButton}`}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Reanalyze">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onReanalyze(contract.id)
                                                }
                                                className={`${classes.actionButton} ${classes.reanalyzeButton}`}
                                                disabled={processingIds.has(
                                                    contract.id,
                                                )}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onDelete(contract.id)
                                                }
                                                className={`${classes.actionButton} ${classes.deleteButton}`}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.pagination}
                component="div"
                count={totalCount}
                page={page}
                onChangePage={onPageChange}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={onRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
            />
        </Paper>
    )
}
